import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { MessageCircle, Share2, Eye } from 'lucide-react';
import { portfolioData, contactData, whatsappMessages } from '../utils/mock';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

export const Portfolio = () => {
  const [filter, setFilter] = useState('All');
  const [titleRef, titleVisible] = useScrollAnimation({ threshold: 0.3, once: true });
  const [gridRef, gridVisible] = useScrollAnimation({ threshold: 0.1, once: true });
  
  const categories = ['All', 'Residential', 'Commercial'];
  
  const filteredProjects = filter === 'All' 
    ? portfolioData 
    : portfolioData.filter(project => project.category === filter);

  const handleWhatsAppShare = (project) => {
    const phoneNumber = contactData.phone.replace(/\s+/g, '');
    const message = `Hi! I'm interested in the "${project.title}" project on your website. Can you share more details?`;
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleShareProject = (project) => {
    const shareText = `Check out this amazing ${project.title} by Haarmony Interiors!`;
    const shareUrl = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + contactData.website)}`;
    window.open(shareUrl, '_blank');
  };

  return (
    <section id="portfolio" className="py-20 bg-gradient-to-b from-rose-50/30 to-white relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-rose-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-200/30 rounded-full blur-3xl"></div>
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div
          ref={titleRef}
          className={`text-center mb-12 transition-all duration-1000 ${
            titleVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Our Portfolio
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            Explore our collection of thoughtfully designed spaces
          </p>

          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setFilter(category)}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 transform hover:scale-105 ${
                  filter === category
                    ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-lg scale-105'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 shadow-sm'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Portfolio Grid */}
        <div ref={gridRef} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project, index) => (
            <Card
              key={project.id}
              className={`group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-700 bg-white rounded-2xl ${
                gridVisible 
                  ? 'opacity-100 scale-100' 
                  : 'opacity-0 scale-90'
              }`}
              style={{ 
                transitionDelay: gridVisible ? `${index * 100}ms` : '0ms'
              }}
            >
              <div className="relative h-80 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:brightness-75"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                {/* Hover Content */}
                <div className="absolute inset-0 p-6 flex flex-col justify-end translate-y-8 group-hover:translate-y-0 transition-transform duration-500">
                  <span className="inline-block bg-gradient-to-r from-rose-500 to-pink-500 text-white text-xs font-medium px-3 py-1 rounded-full mb-2 w-fit shadow-lg">
                    {project.category}
                  </span>
                  <h3 className="text-xl font-bold text-white mb-2 transform group-hover:translate-x-2 transition-transform duration-300">
                    {project.title}
                  </h3>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-3">
                    <Button
                      onClick={() => handleWhatsAppShare(project)}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 text-sm rounded-lg shadow-lg transition-all duration-300 group/btn"
                    >
                      <MessageCircle className="h-4 w-4 mr-1 group-hover/btn:scale-110 transition-transform" />
                      Get Details
                    </Button>
                    <Button
                      onClick={() => handleShareProject(project)}
                      variant="outline"
                      className="bg-white/90 hover:bg-white border-0 text-gray-900 py-2 px-3 rounded-lg shadow-lg transition-all duration-300"
                    >
                      <Share2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center bg-gradient-to-r from-rose-500 to-pink-500 rounded-2xl p-8 shadow-2xl">
          <h3 className="text-3xl font-bold text-white mb-4">
            Love What You See?
          </h3>
          <p className="text-white/90 mb-6 max-w-2xl mx-auto">
            Let's discuss your project on WhatsApp and bring your vision to life!
          </p>
          <Button
            onClick={() => {
              const phoneNumber = contactData.phone.replace(/\s+/g, '');
              window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(whatsappMessages.portfolio)}`, '_blank');
            }}
            className="bg-white text-rose-600 hover:bg-gray-50 px-8 py-6 text-lg rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 group"
          >
            <MessageCircle className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform" />
            Discuss Your Project on WhatsApp
          </Button>
        </div>
      </div>
    </section>
  );
};
